/** @jsxImportSource @emotion/react */
import { useEffect, useCallback } from 'react'
import { useAtomValue } from 'jotai'
import { ManageAccounts } from '@mui/icons-material'
import { useQueryClient } from '@tanstack/react-query'
import { Box, Typography } from '@mui/material'

import { Button } from '@app/components/Atoms/Button'
import { toast, handleClose } from '@app/components/ui/Toast/toast'

import { clientNameAtom } from '@app/state/clientsAtom'
import { USERS_QUERY_KEY } from '../utils/Admin.constants'
import { UserRole, UserProfile } from '@app/types'
import { temporaryRoleToast } from '../styles/Admin.styles'

export function useChangeTempRole() {
  const queryClient = useQueryClient()
  const clientName = useAtomValue(clientNameAtom)

  function setTempRole(userTempRole: string[]) {
    if (userTempRole.length) {
      localStorage.setItem('temp_user_role', JSON.stringify(userTempRole))
      showTempRoles(JSON.stringify(userTempRole))
    }
  }

  const showTempRoles = useCallback(
    (temp_roles: string) => {
      queryClient.setQueryData<UserProfile>([USERS_QUERY_KEY, clientName ?? ''], (old) =>
        old ? { ...old, user_roles: JSON.parse(temp_roles) as UserRole[] } : old,
      )
      queryClient.invalidateQueries({ queryKey: ['layers', clientName] })

      toast({
        title: 'Temporary Role Set',
        position: 'top-center',
        duration: null,
        showClose: false,
        children: (
          <Box css={temporaryRoleToast}>
            <Box>
              <Typography variant="body1" css={{ color: 'white' }}>
                {`Temporary roles set to ${JSON.parse(temp_roles).join(', ')}.`}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                handleClose()
                localStorage.removeItem('temp_user_role')
                queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY, clientName] })
              }}
              startIcon={<ManageAccounts />}
            >
              Reset
            </Button>
          </Box>
        ),
      })
    },
    [clientName, queryClient],
  )

  useEffect(() => {
    const temp_roles = localStorage.getItem('temp_user_role')

    if (temp_roles && Array.isArray(JSON.parse(temp_roles))) {
      showTempRoles(temp_roles)
    }
  }, [showTempRoles])

  return { setTempRole }
}
